import { Component, ElementRef, Input, OnChanges, OnDestroy, SimpleChanges, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import type { ApexOptions } from 'apexcharts';

@Component({
  selector: 'tidy-lazy-chart',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div #chartContainer></div>
  `,
  styles: [`
    :host {
      display: block;
    }
  `]
})
export class LazyChartComponent implements OnChanges, OnDestroy {
  @ViewChild('chartContainer', { static: true }) chartContainer: ElementRef<HTMLDivElement>;
  @Input() series: ApexOptions['series'];
  @Input() chart: ApexOptions['chart'];
  @Input() xaxis: ApexOptions['xaxis'];
  @Input() yaxis: ApexOptions['yaxis'];
  @Input() stroke: ApexOptions['stroke'];
  @Input() grid: ApexOptions['grid'];
  @Input() tooltip: ApexOptions['tooltip'];
  @Input() colors: ApexOptions['colors'];
  @Input() plotOptions: ApexOptions['plotOptions'];
  @Input() fill: ApexOptions['fill'];
  @Input() markers: ApexOptions['markers'];
  @Input() legend: ApexOptions['legend'];
  @Input() dataLabels: ApexOptions['dataLabels'];

  private chartInstance: ApexCharts | null = null;
  private ApexChartsClass: typeof ApexCharts | null = null;

  async ngOnChanges(changes: SimpleChanges): Promise<void> {
    if (!this.series || !this.chart) {
      return;
    }
    await this.renderChart();
  }

  ngOnDestroy(): void {
    this.destroyChart();
  }

  private async renderChart(): Promise<void> {
    try {
      if (!this.ApexChartsClass) {
        const module = await import('apexcharts');
        this.ApexChartsClass = module.default;
      }
      this.destroyChart();
      const options: ApexOptions = {
        series: this.series,
        chart: this.chart,
        xaxis: this.xaxis,
        yaxis: this.yaxis,
        stroke: this.stroke,
        grid: this.grid,
        tooltip: this.tooltip,
      };
      if (this.colors) {options.colors = this.colors;}
      if (this.plotOptions) {options.plotOptions = this.plotOptions;}
      if (this.fill) {options.fill = this.fill;}
      if (this.markers) {options.markers = this.markers;}
      if (this.legend) {options.legend = this.legend;}
      if (this.dataLabels) {options.dataLabels = this.dataLabels;}
      this.chartInstance = new this.ApexChartsClass(this.chartContainer.nativeElement, options);
      await this.chartInstance.render();

      // ApexCharts measures container width once on render. If the parent
      // was not yet at its final size (lazy-loaded card, scrolled into view,
      // CSS still settling), the plot ends up narrower than the card. Calling
      // `resize()` after layout flushes asks ApexCharts to re-read the current
      // container width and re-render to fit.
      //
      // NOTE: do NOT set an explicit pixel width via `updateOptions({ width })`
      // — that locks the chart to a fixed size that can exceed the container
      // on narrow viewports and push the wrapping card past the screen.
      requestAnimationFrame(() => {
        // `resize()` is a real ApexCharts method but isn't on the typed
        // surface — cast through `any` so TS stays out of the way.
        (this.chartInstance as any)?.resize?.();
      });

    } catch (error) {
      console.error('Failed to load ApexCharts:', error);

    }
  }

  private destroyChart(): void {
    if (this.chartInstance) {
      this.chartInstance.destroy();
      this.chartInstance = null;
    }
  }
}
